/**
 * AssistantContext — открытость панели AI-ассистента (US-10 FR-1.1).
 *
 * Отдельный контекст нужен потому, что точек входа больше одной: плавающая
 * кнопка ✨ и «искорка» в строке поиска. Поиск в шапке сейчас закомментирован
 * как заглушка до появления API — когда он вернётся, кнопке-искорке хватит
 * `openAssistant()` отсюда.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface AssistantContextValue {
  open: boolean;
  openAssistant: () => void;
  closeAssistant: () => void;
  setOpen: (open: boolean) => void;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

export const AssistantProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const openAssistant = useCallback(() => setOpen(true), []);
  const closeAssistant = useCallback(() => setOpen(false), []);
  const value = useMemo(
    () => ({ open, openAssistant, closeAssistant, setOpen }),
    [open, openAssistant, closeAssistant],
  );
  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};

export const useAssistant = (): AssistantContextValue => {
  const ctx = useContext(AssistantContext);
  if (!ctx) {
    throw new Error('useAssistant must be used inside <AssistantProvider>');
  }
  return ctx;
};
